import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Radio, ScrollText, ShieldCheck, LogOut, Menu, X, CreditCard, Code2, Inbox, Gift, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Overview } from "@/components/dashboard/Overview";
import { Campaigns } from "@/components/dashboard/Campaigns";
import { FraudLog } from "@/components/dashboard/FraudLog";
import { Billing } from "@/components/dashboard/Billing";
import { Tracking } from "@/components/dashboard/Tracking";
import { Leads } from "@/components/dashboard/Leads";
import { Onboarding } from "@/components/dashboard/Onboarding";
import { Referrals } from "@/components/dashboard/Referrals";
import { EmailAnalytics } from "@/components/dashboard/EmailAnalytics";
import { CS } from "@/constants/testIds/clickshield";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, testid: CS.dashOverviewTab },
  { id: "tracking", label: "Tracking Setup", icon: Code2, testid: CS.dashTrackingTab },
  { id: "campaigns", label: "Campaigns", icon: Radio, testid: CS.dashCampaignsTab },
  { id: "fraud", label: "Fraud Log", icon: ScrollText, testid: CS.dashFraudTab },
  { id: "billing", label: "Billing & Reports", icon: CreditCard, testid: CS.dashBillingTab },
  { id: "referrals", label: "Referrals", icon: Gift, testid: "dash-tab-referrals" },
];

const LEADS_TAB = { id: "leads", label: "Leads", icon: Inbox, testid: "dash-tab-leads" };
const ANALYTICS_TAB = { id: "analytics", label: "Email Analytics", icon: BarChart3, testid: "dash-tab-analytics" };

const TITLES = { fraud: "Fraud Log", billing: "Billing & Reports", tracking: "Tracking Setup", leads: "Lead Inbox", referrals: "Referral Rewards", analytics: "Email Analytics" };

export default function Dashboard() {
  const [tab, setTab] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadLeads, setUnreadLeads] = useState(0);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const tabs = isAdmin ? [...TABS, LEADS_TAB, ANALYTICS_TAB] : TABS;

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/leads").then((r) => setUnreadLeads(r.data.unread)).catch(() => {});
    }
  }, [isAdmin]);

  const doLogout = async () => {
    nav("/");
    await logout();
  };

  const planLabel = user?.plan ? `${user.plan[0].toUpperCase()}${user.plan.slice(1)}` : "Trial";

  const Nav = () => (
    <nav className="flex flex-col gap-1.5">
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => { setTab(t.id); setMobileOpen(false); }}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              active ? "bg-cyan-neon/10 text-cyan-neon border border-cyan-neon/25" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
            }`}
          >
            <Icon className="w-4 h-4" /> {t.label}
            {t.id === "leads" && unreadLeads > 0 && (
              <span data-testid="leads-tab-badge" className="ml-auto px-1.5 py-0.5 rounded-full bg-cyan-neon text-[#040914] font-mono text-[10px] font-bold leading-none">
                {unreadLeads}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#040914] flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/8 bg-[#0B101A] p-5 fixed inset-y-0">
        <button onClick={() => nav("/")} className="flex items-center gap-2.5 mb-8 px-2">
          <ShieldCheck className="w-6 h-6 text-cyan-neon" />
          <span className="font-display text-lg text-white">ClickShield<span className="text-cyan-neon"> AI</span></span>
        </button>
        <Nav />
        <div className="mt-auto">
          <div className="rounded-lg bg-[#040914] border border-white/8 p-4 mb-3">
            <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Current plan</div>
            <div className="font-display text-lg text-cyan-neon mt-1">{planLabel}</div>
            <div className="font-mono text-[10px] text-[#39FF14] mt-0.5 uppercase">{user?.plan_status}</div>
          </div>
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-cyan-neon/15 border border-cyan-neon/30 flex items-center justify-center font-display text-sm text-cyan-neon">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid={CS.dashLogout}
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 backdrop-blur-xl bg-[#040914]/80 border-b border-white/10 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-cyan-neon" />
          <span className="font-display text-white">ClickShield AI</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="text-white">{mobileOpen ? <X /> : <Menu />}</button>
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed top-14 inset-x-0 z-40 bg-[#0B101A] border-b border-white/10 p-4">
          <Nav />
          <button onClick={doLogout} className="mt-4 w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-red-400"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-64 px-5 lg:px-8 py-8 pt-20 lg:pt-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-7 hidden lg:block">
            <h1 className="font-display text-3xl text-white capitalize">{TITLES[tab] || tab}</h1>
            <p className="text-sm text-slate-500 mt-1">Welcome back, {user?.name?.split(" ")[0]}. Your shield is active.</p>
          </div>
          <Onboarding onGo={setTab} />
          {tab === "overview" && <Overview onSetup={() => setTab("tracking")} />}
          {tab === "tracking" && <Tracking />}
          {tab === "campaigns" && <Campaigns />}
          {tab === "fraud" && <FraudLog />}
          {tab === "billing" && <Billing />}
          {tab === "referrals" && <Referrals />}
          {tab === "leads" && isAdmin && <Leads onUnreadChange={setUnreadLeads} />}
          {tab === "analytics" && isAdmin && <EmailAnalytics />}
        </div>
      </main>
    </div>
  );
}
